import { useState, useEffect, useRef } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import "@livekit/components-styles";
import { getPublicApiOrigin } from "../config/env";

const C = {
  bg: "#F9FAFB",
  surface: "#FFFFFF",
  border: "#E5E7EB",
  brand: "#6366F1",
  t1: "#111827",
  t2: "#374151",
  t3: "#6B7280",
  green: "#10B981",
  red: "#EF4444",
  amber: "#F59E0B",
};

const API_BASE = getPublicApiOrigin();

function CandidateTile({ session, expanded, onExpand }) {
  const videoRef = useRef(null);
  const roomRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function connect() {
      try {
        const token = localStorage.getItem("token");
        const url = `${API_BASE}/api/v1/livekit/admin-token/${session.session_id}`;
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`Token fetch failed: ${resp.status}`);
        const { token: livekitToken, url: wsUrl } = await resp.json();

        const room = new Room({
          rtcConfig: {
            iceTransportPolicy: 'relay',
          },
        });
        roomRef.current = room;

        room.on(RoomEvent.TrackSubscribed, (track, _pub, _participant) => {
          if (cancelled) return;
          if (track.kind === Track.Kind.Video && videoRef.current) {
            track.attach(videoRef.current);
            setHasVideo(true);
          }
        });

        room.on(RoomEvent.TrackUnsubscribed, (track) => {
          if (track.kind === Track.Kind.Video) {
            track.detach();
            setHasVideo(false);
          }
        });

        room.on(RoomEvent.Disconnected, () => {
          if (!cancelled) setConnected(false);
        });

        await room.connect(wsUrl, livekitToken);
        if (!cancelled) setConnected(true);

        room.remoteParticipants.forEach((participant) => {
          participant.trackPublications.forEach((pub) => {
            if (pub.track?.kind === Track.Kind.Video && videoRef.current) {
              pub.track.attach(videoRef.current);
              setHasVideo(true);
            }
          });
        });
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    }

    connect();
    return () => {
      cancelled = true;
      if (roomRef.current) {
        roomRef.current.disconnect();
        roomRef.current = null;
      }
    };
  }, [session.session_id]);

  const elapsed = session.started_at
    ? Math.floor((Date.now() - new Date(session.started_at)) / 60000)
    : 0;

  return (
    <div
      onClick={() => onExpand(session.session_id)}
      style={{
        background: C.surface,
        borderRadius: 12,
        border: `1px solid ${connected ? C.brand : C.border}`,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all .18s",
        boxShadow: expanded ? `0 0 0 3px ${C.brand}30` : "none",
      }}
    >
      <div
        style={{
          position: "relative",
          background: "#0F172A",
          aspectRatio: "4/3",
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />

        {!hasVideo && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "#1E293B",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            >
              👤
            </div>
            <span style={{ fontSize: 12, color: "#475569" }}>
              {error ? "Connection failed" : "Connecting..."}
            </span>
          </div>
        )}

        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: "rgba(0,0,0,.6)",
            padding: "3px 8px",
            borderRadius: 20,
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: connected ? C.green : C.amber,
            }}
          />
          <span style={{ fontSize: 10, color: "white", fontWeight: 600 }}>
            {connected ? "LIVE" : "CONNECTING"}
          </span>
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 8,
            right: 8,
            background: "rgba(0,0,0,.6)",
            padding: "3px 8px",
            borderRadius: 20,
            fontSize: 10,
            color: "white",
          }}
        >
          Q{(session.current_question || 0) + 1}/{session.total_questions}
        </div>
      </div>

      <div style={{ padding: "10px 12px" }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: C.t1,
            marginBottom: 2,
          }}
        >
          {session.candidate_name}
        </div>
        <div style={{ fontSize: 11, color: C.t3 }}>
          {session.role} · {elapsed}m elapsed
        </div>
      </div>
    </div>
  );
}

export default function LiveMonitor() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  async function fetchSessions() {
    try {
      const token = localStorage.getItem("token");
      const url = `${API_BASE}/api/v1/livekit/active-sessions`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(resp.status);
      const data = await resp.json();
      setSessions(data.sessions || []);
      setLastRefresh(new Date());
    } catch (e) {
      console.error("Failed to fetch active sessions:", e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleExpand = (id) => setExpanded((e) => (e === id ? null : id));

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "26px 24px",
        background: C.bg,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 22,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: C.t1,
              marginBottom: 3,
            }}
          >
            Live Monitor
          </h1>
          <p style={{ fontSize: 13, color: C.t3 }}>
            {sessions.length} active interview{sessions.length !== 1 ? "s" : ""}
            &nbsp;·&nbsp;
            <span style={{ color: C.t3 }}>
              Updated{" "}
              {lastRefresh.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={fetchSessions}
          style={{
            padding: "7px 16px",
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            background: "white",
            color: C.t2,
            border: `1px solid ${C.border}`,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          ↻ Refresh
        </button>
      </div>

      {!loading && sessions.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 0",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 40 }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.t1 }}>
            No active interviews
          </div>
          <div style={{ fontSize: 13, color: C.t3 }}>
            Candidates currently in interviews will appear here live.
          </div>
        </div>
      )}

      {sessions.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: expanded
              ? "1fr"
              : "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {sessions
            .filter((s) => !expanded || s.session_id === expanded)
            .map((s) => (
              <CandidateTile
                key={s.session_id}
                session={s}
                expanded={expanded === s.session_id}
                onExpand={handleExpand}
              />
            ))}
        </div>
      )}

      {expanded && (
        <button
          type="button"
          onClick={() => setExpanded(null)}
          style={{
            marginTop: 16,
            padding: "8px 18px",
            borderRadius: 7,
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            background: C.brand,
            color: "white",
            border: "none",
          }}
        >
          ← Back to grid
        </button>
      )}
    </div>
  );
}
